import { useEffect, useMemo, useState } from 'react'
import { HorizontalLoader } from 'Components'
import { handleError } from 'Helpers'
import { 
  PendingPayoutUserXModel,
  PayoutQueueParams,
} from 'BoostXpressAdmin/Model/payoutXModel'
import { payoutBXService } from 'BoostXpressAdmin/Services/payoutBXService'
import './Payouts.scss'
 

const PAGE_SIZE = 50

const extractPagination = (
  meta: any,
  count: number,
  fallbackPage: number,
  fallbackLimit: number,
) => {
  const pagination = meta?.pagination ?? meta?.data?.pagination ?? null
  const total =
    pagination?.total ??
    pagination?.totalItems ??
    pagination?.total_count ??
    pagination?.totalCount ??
    pagination?.totalRecords ??
    meta?.total ??
    meta?.totalItems ??
    meta?.total_count ??
    meta?.data?.total ??
    pagination?.count ??
    count

  return {
    total: Number(total),
    perPage: Number(
      pagination?.per_page ?? pagination?.limit ?? meta?.per_page ?? meta?.limit ?? fallbackLimit,
    ),
    currentPage: Number(
      pagination?.current_page ??
        pagination?.page ??
        meta?.current_page ??
        meta?.page ??
        fallbackPage,
    ),
  }
}

const formatMajorNaira = (value: number) => {
  const formatted = new Intl.NumberFormat('en-NG', { maximumFractionDigits: 0 }).format(value ?? 0)
  return `₦${formatted}`
}

const fullName = (firstName?: string | null, lastName?: string | null) =>
  `${firstName ?? ''} ${lastName ?? ''}`.trim() || '—'

interface PayoutQueueTableProps {
  onRefresh?: () => void
  onMarkSinglePay?: (row: PendingPayoutUserXModel) => void
}

const PayoutQueueTable = ({ onRefresh, onMarkSinglePay }: PayoutQueueTableProps) => {
  const [rows, setRows] = useState<PendingPayoutUserXModel[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [limit] = useState(PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [includeWithoutBankInfo, setIncludeWithoutBankInfo] = useState(true)
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [showFilters, setShowFilters] = useState(false)

  const loadQueue = async () => {
    try {
      setIsLoading(true)
      const params: PayoutQueueParams = {
        page,
        limit,
        search: search.trim() || undefined,
        includeWithoutBankInfo,
        sortOrder,
      }

      const res = await payoutBXService.pendingPayouts(params)
      const data = Array.isArray(res.data?.data) ? res.data.data : []
      const pagination = extractPagination(res.data?.meta, data.length, page, limit)

      setRows(data)
      setTotal(pagination.total)
    } catch (e) {
      setRows([])
      setTotal(0)
      handleError({ response: { data: { error: 'Failed to fetch payout queue.' } } })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadQueue()
  }, [page, limit, search, includeWithoutBankInfo, sortOrder])

  

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || rows.length || 1) / limit)),
    [limit, rows.length, total],
  )

  const resetFilters = () => {
    setIncludeWithoutBankInfo(true)
    setSortOrder('DESC')
    setPage(1)
  }

  const handleExportCsv = () => {
    const csv = rows.map((row) => ({
      attendant: fullName(row.firstName, row.lastName),
      email: row.email,
      phone: row.phone,
      station: row.fillingStation?.businessName || row.fillingStation?.branchName || '—',
      country: row.country || '—',
      availableAmount: row.availableMajor ?? 0,
      bankName: row.bankInfo?.bankName || '—',
      bankAccount: row.bankInfo?.bankAccountNumber || '—',
    }))

    // Simple CSV export
    const headers = Object.keys(csv[0] || {}).join(',')
    const rowsData = csv.map((row) => Object.values(row).join(',')).join('\n')
    const csvContent = `${headers}\n${rowsData}`

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `boostxpress_payout_queue_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  return (
    <div className='bx-payout-board'>
      <div className='bx-payout-board__toolbar row g-2 align-items-center'>
        <div className='col-12 col-lg'>
          <div className='bx-payout-board__search'>
            <span className='bx-payout-board__search-icon'>
              <svg
                width='17'
                height='17'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                  stroke='#868484'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </span>
            <input
              type='text'
              placeholder='Search attendant name'
              className='bx-payout-board__search-input'
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
            />
          </div>
        </div>

        <div className='col-6 col-lg-auto position-relative'>
          <button
            type='button'
            className='bx-payout-board__action-btn w-100'
            onClick={() => setShowFilters((value) => !value)}
          >
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M2 4H14M2 8H10M2 12H6'
                stroke='#667085'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <span>Filters</span>
          </button>

          {showFilters && (
            <div className='bx-payout-board__filters-dropdown'>
              <div className='bx-payout-board__filters-header'>
                <h5>Queue Filters</h5>
                <button type='button' onClick={() => setShowFilters(false)}>
                  x
                </button>
              </div>

              <div className='bx-payout-board__filters-body'>
                <label>
                  Sort Order
                  <select
                    className='form-control'
                    value={sortOrder}
                    onChange={(e) => {
                      setSortOrder(e.target.value as 'ASC' | 'DESC')
                      setPage(1)
                    }}
                  >
                    <option value='DESC'>DESC</option>
                    <option value='ASC'>ASC</option>
                  </select>
                </label>

                <label className='bx-payout-board__checkbox'>
                  <input
                    type='checkbox'
                    checked={includeWithoutBankInfo}
                    onChange={(e) => {
                      setIncludeWithoutBankInfo(e.target.checked)
                      setPage(1)
                    }}
                  />
                  <span>Include attendants without bank info</span>
                </label>
              </div>

              <div className='bx-payout-board__filters-footer'>
                <button
                  type='button'
                  className='btn btn-default payout-filter-btn payout-filter-btn--ghost'
                  style={{ backgroundColor: 'grey' }}
                  onClick={resetFilters}
                >
                  Reset
                </button>
                <button
                  type='button'
                  className='btn btn-default bg-default payout-filter-btn'
                  onClick={() => setShowFilters(false)}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        <div className='col-6 col-lg-auto'>
          <button
            type='button'
            className='bx-payout-board__action-btn w-100'
            onClick={handleExportCsv}
            disabled={isLoading || !rows.length}
          >
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M2.66667 10.6667V12C2.66667 12.3536 2.79028 12.5891 2.97631 12.7751C3.16234 12.9612 3.39786 13.0847 3.66667 13.0847H12.3333C12.6021 13.0847 12.8377 12.9612 13.0237 12.7751C13.2097 12.5891 13.3333 12.3536 13.3333 12V10.6667'
                stroke='#667085'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M5.33333 5.33333L8 8L10.6667 5.33333'
                stroke='#667085'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M8 2.66667V8'
                stroke='#667085'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <span>Export</span>
          </button>
        </div>
      </div>

      <div className='bx-payout-board__table-shell border rounded overflow-hidden'>
        {isLoading ? (
          <div className='bx-payout-board__loader'>
            <HorizontalLoader />
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='bx-payout-board__table'>
              <thead>
                <tr>
                  <th>SN</th>
                  <th>Attendant</th>
                  <th>Station</th>
                  <th>Country</th>
                  <th>Available Amount</th>
                  <th>Bank Name</th>
                  <th>Bank Account</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className='text-center'>
                      No pending payouts found
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={row.resourceId}>
                      <td>{Number(index) + 1 + (page - 1) * limit}</td>
                      <td>
                        <div className='bx-payout-board__primary-text'>
                          {fullName(row.firstName, row.lastName)}
                        </div>
                        <div className='bx-payout-board__secondary-text'>
                          {row.email || row.phone || '—'}
                        </div>
                      </td>
                      <td>
                        {row.fillingStation?.businessName ||
                          row.fillingStation?.branchName ||
                          '—'}
                      </td>
                      <td>{row.country || '—'}</td>
                      <td>{formatMajorNaira(row.availableMajor ?? 0)}</td>
                      <td>{row.bankInfo?.bankName || '—'}</td>
                      <td>{row.bankInfo?.bankAccountNumber || '—'}</td>
                      <td>
                        <span
                          className={`bx-payout-board__status-pill ${
                            row.bankInfo?.bankAccountNumber
                              ? 'bx-payout-board__status-pill--success'
                              : 'bx-payout-board__status-pill--warning'
                          }`}
                        >
                          {row.bankInfo?.bankAccountNumber ? 'Ready' : 'No Bank Info'}
                        </span>
                      </td>
                      <td>
                        <button
                          type='button'
                          className='bx-payout-board__table-action'
                          onClick={() => onMarkSinglePay?.(row)}
                        >
                          Mark Paid
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className='bx-payout-board__pagination'>
        <button
          type='button'
          className='bx-payout-board__pagination-btn'
          disabled={page <= 1 || isLoading}
          onClick={() => setPage((value) => Math.max(1, value - 1))}
        >
          Previous
        </button>

        <div className='bx-payout-board__pagination-pages'>
          Page {page} of {totalPages}
        </div>

        <button
          type='button'
          className='bx-payout-board__pagination-btn'
          disabled={isLoading || page >= totalPages}
          onClick={() => setPage((value) => value + 1)}
        >
          Next
        </button>
      </div>
    </div>
  )
}

export default PayoutQueueTable
